import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import i18next from 'i18next';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: '#F8F9FA',
        borderRadius: '8px',
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
    },
    list: {
        listStyleType: 'none',
        padding: 0,
        margin: 0,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
        fontSize: '0.875rem',
        transition: 'color 0.3s ease',
    },
    icon: {
        fontSize: '1rem',
        marginRight: theme.spacing(1),
        transition: 'color 0.3s ease',
    },
    met: {
        color: theme.palette.success.main,
    },
    unmet: {
        color: theme.palette.text.secondary,
    }
}));

interface PasswordRequirementsProps {
    password?: string;
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password = '' }) => {
    const classes = useStyles();

    const requirements = useMemo(() => [
        {
            id: 'length',
            label: i18next.t('common:login.passwordRequirements.length'),
            isMet: password.length >= 8,
        },
        {
            id: 'lowercase',
            label: i18next.t('common:login.passwordRequirements.lowercase'),
            isMet: /[a-z]/.test(password),
        },
        {
            id: 'uppercase',
            label: i18next.t('common:login.passwordRequirements.uppercase'),
            isMet: /[A-Z]/.test(password),
        },
        {
            id: 'number',
            label: i18next.t('common:login.passwordRequirements.number'),
            isMet: /\d/.test(password),
        },
        {
            id: 'special',
            label: i18next.t('common:login.passwordRequirements.special'),
            isMet: /[@$!%*?&*\- _+={}\[\]|:;"'<>,.?/~`]/.test(password),
        },
    ], [password]);

    return (
        <Box className={classes.root} aria-live="polite">
            <Typography className={classes.title}>
                {i18next.t('common:login.passwordRequirements.title')}
            </Typography>
            <ul className={classes.list}>
                {requirements.map((req) => (
                    <li
                        key={req.id}
                        className={`${classes.listItem} ${req.isMet ? classes.met : classes.unmet}`}
                        aria-label={`${req.label}: ${req.isMet ? 'met' : 'not met'}`}
                    >
                        {req.isMet ? (
                            <CheckCircle className={`${classes.icon} ${classes.met}`} />
                        ) : (
                            <Cancel className={`${classes.icon} ${classes.unmet}`} />
                        )}
                        {req.label}
                    </li>
                ))}
            </ul>
        </Box>
    );
};
